import { api } from './api';
import { FULL_REVIEW_TEMPLATES_PATH } from './templateApiPaths';
export type {
  AdminAssociationInput,
  AdminQuestionField,
  AdminQuestionsSchema,
  AdminQuestionsSchemaInput,
  AdminReviewCategory,
  AdminReviewCategoryInput,
  AdminReviewCategoryUpdate,
  AdminReviewItem,
  AdminReviewItemInput,
  AdminReviewItemUpdate,
  AdminReviewTemplate,
  AdminReviewTemplateInput,
  AdminVendor,
  AdminVendorInput,
  AdminVendorType,
  AdminVendorTypeInput,
  AdminVendorTypeUpdate,
  AdminVendorUpdate,
} from '@exhale/contracts';

import type {
  AdminAssociationInput,
  AdminReviewCategory,
  AdminReviewCategoryInput,
  AdminReviewCategoryUpdate,
  AdminReviewItem,
  AdminReviewItemInput,
  AdminReviewItemUpdate,
  AdminReviewTemplate,
  AdminReviewTemplateInput,
  AdminVendor,
  AdminVendorInput,
  AdminVendorType,
  AdminVendorTypeInput,
  AdminVendorTypeUpdate,
  AdminVendorUpdate,
} from '@exhale/contracts';

export const adminService = {
  listItems<T = AdminReviewItem>(includeDeprecated = false): Promise<T[]> {
    return api.get<T[]>(`/admin/items?includeDeprecated=${includeDeprecated}`);
  },

  createItem<T = AdminReviewItem>(itemData: AdminReviewItemInput): Promise<T> {
    return api.post<T>('/admin/items', itemData);
  },

  updateItem<T = AdminReviewItem>(itemId: number, updates: AdminReviewItemUpdate): Promise<T> {
    return api.put<T>(`/admin/items/${itemId}`, updates);
  },

  deprecateItem<T = AdminReviewItem>(itemId: number): Promise<T> {
    return api.post<T>(`/admin/items/${itemId}/deprecate`);
  },

  restoreItem<T = AdminReviewItem>(itemId: number): Promise<T> {
    return api.post<T>(`/admin/items/${itemId}/restore`);
  },

  listCategories<T = AdminReviewCategory>(): Promise<T[]> {
    return api.get<T[]>('/admin/categories');
  },

  createCategory<T = AdminReviewCategory>(categoryData: AdminReviewCategoryInput): Promise<T> {
    return api.post<T>('/admin/categories', categoryData);
  },

  updateCategory<T = AdminReviewCategory>(
    categoryId: number,
    updates: AdminReviewCategoryUpdate
  ): Promise<T> {
    return api.put<T>(`/admin/categories/${categoryId}`, updates);
  },

  deleteCategory(categoryId: number, moveToCategory?: number): Promise<void> {
    return api.delete<void>(`/admin/categories/${categoryId}`, {
      data: moveToCategory ? { moveToCategory } : undefined,
    });
  },

  listReviewTemplates<T = AdminReviewTemplate>(): Promise<T[]> {
    return api.get<T[]>(FULL_REVIEW_TEMPLATES_PATH);
  },

  listAdminTemplates<T = AdminReviewTemplate>(): Promise<T[]> {
    return api.get<T[]>('/admin/templates');
  },

  createTemplate<T = AdminReviewTemplate>(templateData: AdminReviewTemplateInput): Promise<T> {
    return api.post<T>('/admin/templates', templateData);
  },

  updateTemplate<T = AdminReviewTemplate>(
    templateId: number,
    updates: Partial<AdminReviewTemplateInput>
  ): Promise<T> {
    return api.put<T>(`/admin/templates/${templateId}`, updates);
  },

  cloneTemplate<T = AdminReviewTemplate>(
    templateId: number,
    templateData: AdminReviewTemplateInput
  ): Promise<T> {
    return api.post<T>(`/admin/templates/${templateId}/clone`, templateData);
  },

  deleteTemplate(templateId: number): Promise<void> {
    return api.delete<void>(`/admin/templates/${templateId}`);
  },

  listVendors<T = AdminVendor>(): Promise<T[]> {
    return api.get<T[]>('/admin/vendors');
  },

  createVendor<T = AdminVendor>(vendorData: AdminVendorInput): Promise<T> {
    return api.post<T>('/admin/vendors', vendorData);
  },

  updateVendor<T = AdminVendor>(vendorId: number, updates: AdminVendorUpdate): Promise<T> {
    return api.put<T>(`/admin/vendors/${vendorId}`, updates);
  },

  listVendorTypes<T = AdminVendorType>(): Promise<T[]> {
    return api.get<T[]>('/admin/vendor-types');
  },

  createVendorType<T = AdminVendorType>(vendorTypeData: AdminVendorTypeInput): Promise<T> {
    return api.post<T>('/admin/vendor-types', vendorTypeData);
  },

  updateVendorType<T = AdminVendorType>(
    vendorTypeId: number,
    updates: AdminVendorTypeUpdate
  ): Promise<T> {
    return api.put<T>(`/admin/vendor-types/${vendorTypeId}`, updates);
  },

  listTemplateVendors<T = AdminVendor>(templateId: number): Promise<T[]> {
    return api.get<T[]>(`/admin/templates/${templateId}/vendors`);
  },

  addVendorToTemplate(
    templateId: number,
    vendorId: number,
    association: AdminAssociationInput = {}
  ): Promise<void> {
    return api.post<void>(`/admin/templates/${templateId}/vendors`, {
      vendorId,
      ...association,
    });
  },

  removeVendorFromTemplate(templateId: number, vendorId: number): Promise<void> {
    return api.delete<void>(`/admin/templates/${templateId}/vendors/${vendorId}`);
  },

  listVendorTemplates<T = AdminReviewTemplate>(vendorId: number): Promise<T[]> {
    return api.get<T[]>(`/admin/vendors/${vendorId}/templates`);
  },

  addTemplateToVendor(
    vendorId: number,
    templateId: number,
    association: AdminAssociationInput = {}
  ): Promise<void> {
    return api.post<void>(`/admin/vendors/${vendorId}/templates`, {
      templateId,
      ...association,
    });
  },

  removeTemplateFromVendor(vendorId: number, templateId: number): Promise<void> {
    return api.delete<void>(`/admin/vendors/${vendorId}/templates/${templateId}`);
  },
};
